import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { AnalysisStage, AnalysisStatusResponse } from '@/types/analysisProgress';

interface UseAnalysisProgressResult {
  stage: AnalysisStage;
  progress: number;
  statusText: string;
  isLoading: boolean;
  error: Error | null;
  estimatedTimeRemaining?: number;
}

/**
 * Hook to poll analysis progress every 3 seconds
 * Stops polling when stage === 'complete'
 */
export function useAnalysisProgress(swingRunId: string): UseAnalysisProgressResult {
  const [stage, setStage] = useState<AnalysisStage>('uploading');
  const [progress, setProgress] = useState<number>(0);
  const [statusText, setStatusText] = useState<string>('Uploading video...');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<number | undefined>();

  const fetchStatus = useCallback(async () => {
    console.log('[useAnalysisProgress] 🔄 fetchStatus called with swingRunId:', swingRunId);
    if (!swingRunId) {
      console.log('[useAnalysisProgress] No swingRunId provided, setting error');
      setError(new Error('No runId provided'));
      setIsLoading(false);
      return;
    }

    try {
      // Call edge function - since verify_jwt = false, we can call without auth
      // But we'll still try to get session for logging purposes
      const { data: { session } } = await supabase.auth.getSession();
      
      // Build the function URL
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('VITE_SUPABASE_URL environment variable is not set');
      }
      const url = `${supabaseUrl}/functions/v1/analysis-status?runId=${swingRunId}`;
      console.log('[useAnalysisProgress] 🌐 Making request to:', url);
      
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      // Add auth header if available (even though verify_jwt = false)
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[useAnalysisProgress] API error:', {
          status: response.status,
          statusText: response.statusText,
          errorText,
          url,
        });
        throw new Error(`Failed to fetch status: ${response.status} ${errorText}`);
      }

      const status = (await response.json()) as AnalysisStatusResponse;
      
      console.log('[useAnalysisProgress] ✅ Status update received:', {
        swingRunId,
        stage: status.stage,
        progress: status.progress,
        statusText: status.statusText,
        estimatedTimeRemaining: status.estimatedTimeRemaining,
        responseStatus: response.status,
        responseOk: response.ok
      });
      
      setStage(status.stage);
      setProgress(status.progress);
      setStatusText(status.statusText);
      setEstimatedTimeRemaining(status.estimatedTimeRemaining);
      setError(null);

      // Stop polling when complete
      if (status.stage === 'complete') {
        console.log('[useAnalysisProgress] 🏁 Analysis complete, stopping polling');
        setIsLoading(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error(String(err));
      console.error('[useAnalysisProgress] Error fetching status:', errorMessage);
      // Set error but don't block UI - continue showing tracker with last known state
      setError(errorMessage);
      // Don't stop polling on error - might be transient
      // Keep showing the tracker even if endpoint fails
    }
  }, [swingRunId]);

  useEffect(() => {
    console.log('[useAnalysisProgress] 🚀 useEffect triggered with swingRunId:', swingRunId);
    if (!swingRunId) {
      // If no runId, set default values and stop loading
      console.log('[useAnalysisProgress] No swingRunId, setting default values');
      setStage('uploading');
      setProgress(0);
      setStatusText('Uploading video...');
      setIsLoading(false);
      return;
    }

    // Initial fetch
    fetchStatus().then(() => {
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });

    // Poll every 3 seconds
    console.log('[useAnalysisProgress] 🔄 Setting up 3-second polling interval');
    const interval = setInterval(() => {
      console.log('[useAnalysisProgress] ⏰ 3-second interval triggered, calling fetchStatus');
      fetchStatus();
    }, 3000);

    // Cleanup
    return () => {
      console.log('[useAnalysisProgress] 🧹 Cleaning up polling interval');
      clearInterval(interval);
    };
  }, [swingRunId, fetchStatus]);

  // Stop polling when complete
  useEffect(() => {
    if (stage === 'complete') {
      // Clear any existing intervals (safety check)
      // The cleanup in the main useEffect should handle this, but this is a backup
    }
  }, [stage]);

  return {
    stage,
    progress,
    statusText,
    isLoading,
    error,
    estimatedTimeRemaining,
  };
}

